import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useQueryClient } from '@tanstack/react-query'
import Icon from '../../../../atoms/Icon'
import FlexRow from '../../../../molecules/FlexRow'
import { getWorkflowStatus } from '../shared/redtailUIHelpers'
import WorkflowDetailModal from './WorkflowDetailModal'
import { workflowDetailKey } from './useWorkflowDetail'

const WORKFLOW_STATUS = {
  complete: { icon: 'checkCircle', color: 'var(--color-growth-500)' },
  overdue: { icon: 'clock', color: 'var(--color-decline-500)' },
  outstanding: { icon: 'radioUnchecked', color: 'var(--text-color-subtle)' }
}

function WorkflowRow ({ workflow, sizes, onClick }) {
  const status = getWorkflowStatus(workflow)
  const cfg = WORKFLOW_STATUS[status]
  const contactName = workflow.linked_contacts?.[0]?.full_name || null
  const dueStr = workflow.due_date ? dayjs(workflow.due_date).format('M/D/YYYY') : null

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      style={{ borderBottom: '1px solid var(--color-neutral-200)', padding: sizes.rowPadding, cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          paddingTop: '2px',
          color: cfg.color
        }}
        >
          <Icon name={cfg.icon} customSize='20px' />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {contactName ? (
            <div style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-color-subtle)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            >
              {contactName}
            </div>
          ) : null}
          <div style={{
            fontSize: sizes.fontSize,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {workflow.name || 'Untitled Workflow'}
          </div>
        </div>
        {dueStr ? (
          <div style={{
            flexShrink: 0,
            textAlign: 'right',
            fontSize: 'var(--font-size-xs)',
            color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)',
            whiteSpace: 'nowrap'
          }}
          >
            {status === 'complete' ? 'Completed' : `Due ${dueStr}`}
          </div>
        ) : null}
      </div>
    </div>
  )
}

WorkflowRow.propTypes = {
  workflow: PropTypes.object.isRequired,
  sizes: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
}

function WorkflowsContent ({ items, sizes }) {
  const queryClient = useQueryClient()
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null)

  useEffect(() => {
    for (const wf of items) {
      queryClient.setQueryData(
        workflowDetailKey(wf.id),
        old => old ?? wf
      )
    }
  }, [items, queryClient])

  const handleCloseDetail = useCallback(() => setSelectedWorkflowId(null), [])

  return (
    <>
      {!items.length
        ? (
          <FlexRow
            className='justify-center items-center'
            style={{
              minHeight: '80px',
              color: 'var(--text-color-subtle)',
              fontSize: 'var(--font-size-sm)'
            }}
          >
            No workflows found
          </FlexRow>
        )
        : items.map(workflow => (
          <WorkflowRow
            key={workflow.id}
            workflow={workflow}
            sizes={sizes}
            onClick={() => setSelectedWorkflowId(workflow.id)}
          />
        ))}

      <WorkflowDetailModal
        open={!!selectedWorkflowId}
        onClose={handleCloseDetail}
        workflowId={selectedWorkflowId}
      />
    </>
  )
}

WorkflowsContent.propTypes = {
  items: PropTypes.array.isRequired,
  sizes: PropTypes.object.isRequired
}

export default WorkflowsContent
